import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Utils,
    authJsonHeaders,
    getPlanCheckoutIntent,
    getPlanCheckoutSignInPath,
    clearPlanCheckoutIntent,
    ANNUAL_BILLING_DISCOUNT_FACTOR,
    calculateGrowthPlanEstimate,
    resolveBillingGeoHeaders,
    DEFAULT_BILLING_CURRENCY,
    formatMetricPrice,
} from '../../utils';
import PlanMetricsList from './PlanMetricsList';
import './payment.css';

function loadRazorpayScript() {
    if (typeof window === 'undefined') return Promise.reject(new Error('no_window'));
    if (window.Razorpay) return Promise.resolve();
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error('razorpay_script_failed'));
        document.body.appendChild(script);
    });
}

const Payment = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [processing, setProcessing] = useState(false);
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [geoHeaders, setGeoHeaders] = useState({});

    const planDetails = useMemo(() => {
        if (location.state?.plan) {
            return location.state.plan;
        }
        const intent = getPlanCheckoutIntent();
        if (intent?.plan) {
            return {
                ...intent.plan,
                billingCycle: intent.billingCycle || 'monthly',
                period: intent.plan.period || '/mo',
            };
        }
        return null;
    }, [location.state]);

    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('userData') || 'null');
        } catch {
            return null;
        }
    }, []);

    const currencySymbol = planDetails?.currency_symbol || DEFAULT_BILLING_CURRENCY?.symbol;
    const baseAmount = Number(planDetails?.price_amount);
    const safeBaseAmount = Number.isFinite(baseAmount) ? baseAmount : 0;

    useEffect(() => {
        if (!userData?.id) {
            if (planDetails) {
                navigate(getPlanCheckoutSignInPath(planDetails, planDetails.billingCycle));
            } else {
                navigate('/signIn?redirect=' + encodeURIComponent('/plan'));
            }
            return;
        }
        if (!planDetails) {
            navigate('/plan');
        }
    }, [userData, planDetails, navigate]);

    useEffect(() => {
        resolveBillingGeoHeaders({ optional: true }).then(setGeoHeaders).catch(() => setGeoHeaders({}));
    }, []);

    const calculateTotal = useCallback(() => {
        const base = Number.isFinite(safeBaseAmount) && safeBaseAmount > 0 ? safeBaseAmount : 0;
        return calculateGrowthPlanEstimate({
            pricing: [{ type: 0, price: base }],
            freeLimits: [],
            usageByType: {},
            billingCycle: planDetails?.billingCycle || 'monthly',
        }).total;
    }, [safeBaseAmount, planDetails?.billingCycle]);

    const formattedTotal = formatMetricPrice(currencySymbol, calculateTotal());

    const handlePay = async () => {
        if (!userData?.id || !planDetails?.id) return;

        setProcessing(true);
        try {
            await loadRazorpayScript();

            const orderRes = await fetch(`${Utils.baseurl}payment/create-order`, {
                method: 'POST',
                headers: { ...authJsonHeaders(), ...geoHeaders },
                body: JSON.stringify({
                    plan_id: planDetails.id,
                    userId: userData.id,
                    billing_cycle: planDetails.billingCycle || 'monthly',
                }),
            });
            const orderResult = await orderRes.json();

            if (!orderResult.status) {
                toast.error(orderResult.message || 'Could not start payment');
                setProcessing(false);
                return;
            }

            // Paid period still running + card already on file — Growth starts at next bill.
            if (orderResult.data?.needs_payment === false) {
                clearPlanCheckoutIntent();
                setPaymentSuccess(true);
                toast.success(orderResult.data.message || 'Plan switch scheduled for next billing date.');
                setProcessing(false);
                return;
            }

            if (!orderResult.data?.order_id) {
                toast.error(orderResult.message || 'Could not start payment');
                setProcessing(false);
                return;
            }

            const {
                key_id, order_id, amount, currency, site_name: siteName,
                customer_id, recurring, invoice_id: checkoutInvoiceId,
                deferred, message: deferredMessage,
            } = orderResult.data;

            const recordCheckoutFailure = async (err = {}, extra = {}) => {
                if (!checkoutInvoiceId) return;
                try {
                    await fetch(`${Utils.baseurl}user/billing/invoices/${checkoutInvoiceId}/payment-attempts`, {
                        method: 'POST',
                        headers: { ...authJsonHeaders(), ...geoHeaders },
                        body: JSON.stringify({
                            source: 'checkout',
                            order_id: extra.order_id || order_id || null,
                            payment_id: err?.metadata?.payment_id || err?.payment_id || extra.payment_id || null,
                            razorpay_status: 'failed',
                            error_code: err?.code || extra.error_code || null,
                            error_reason: err?.reason || extra.error_reason || null,
                            error_description: err?.description || err?.reason || extra.error_description || 'Payment failed',
                            error_message: err?.description || err?.reason || extra.error_message || 'Payment failed',
                        }),
                    });
                } catch (e) {
                    console.warn('recordCheckoutFailure failed', e);
                }
            };

            const options = {
                key: key_id,
                amount,
                currency,
                name: siteName,
                description: `${planDetails.name} plan`,
                order_id,
                // Only pass recurring:1 when backend created a mandate order.
                // Do NOT use `recurring || 1` — that turns 0 into 1.
                ...(customer_id ? { customer_id } : {}),
                ...(Number(recurring) === 1 ? { recurring: 1 } : {}),
                prefill: {
                    email: userData.email || '',
                    name: userData.name || userData.first_name || '',
                    ...(userData?.mobile ? { contact: userData?.mobile } : {}),
                },
                theme: { color: '#4346CE' },
                handler: async (response) => {
                    try {
                        const verifyRes = await fetch(`${Utils.baseurl}payment/verify-payment`, {
                            method: 'POST',
                            headers: { ...authJsonHeaders(), ...geoHeaders },
                            body: JSON.stringify({
                                userId: userData.id,
                                plan_id: planDetails.id,
                                billing_cycle: planDetails.billingCycle || 'monthly',
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                            }),
                        });
                        const verifyResult = await verifyRes.json();
                        if (verifyResult.status) {
                            clearPlanCheckoutIntent();
                            setPaymentSuccess(true);
                            toast.success(
                                deferred && deferredMessage
                                    ? deferredMessage
                                    : 'Payment successful!'
                            );
                        } else {
                            toast.error(verifyResult.message || 'Payment verification failed');
                            await recordCheckoutFailure(
                                {},
                                {
                                    order_id: response.razorpay_order_id,
                                    payment_id: response.razorpay_payment_id,
                                    error_code: 'VERIFY_FAILED',
                                    error_reason: 'verification_failed',
                                    error_description: verifyResult.message || 'Payment verification failed',
                                }
                            );
                        }
                    } catch (err) {
                        console.error(err);
                        toast.error('Payment verification failed');
                        await recordCheckoutFailure(
                            {},
                            {
                                order_id: response?.razorpay_order_id,
                                payment_id: response?.razorpay_payment_id,
                                error_code: 'VERIFY_FAILED',
                                error_reason: 'verification_failed',
                                error_description: err?.message || 'Payment verification failed',
                            }
                        );
                    } finally {
                        setProcessing(false);
                    }
                },
                modal: {
                    ondismiss: () => setProcessing(false),
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', async (resp) => {
                const err = resp?.error || {};
                console.error('Razorpay payment.failed:', err);
                toast.error(err.description || 'Payment failed');
                await recordCheckoutFailure(err, { order_id });
                setProcessing(false);
            });
            rzp.open();
        } catch (error) {
            console.error(error);
            toast.error('Could not open payment gateway');
            setProcessing(false);
        }
    };

    if (!planDetails) {
        return null;
    }

    if (paymentSuccess) {
        return (
            <div className="payment-page">
                <div className="payment-success-container">
                    <div className="success-animation">
                        <svg className="checkmark" viewBox="0 0 52 52">
                            <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
                            <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                        </svg>
                    </div>
                    <h2>Payment Successful!</h2>
                    <p>Thank you for subscribing to the {planDetails.name} plan</p>
                    <button type="button" className="pay-button" onClick={() => navigate('/apps')}>
                        Go to Apps
                    </button>
                </div>
            </div>
        );
    }

    const annualDiscount = safeBaseAmount * 12 * (1 - ANNUAL_BILLING_DISCOUNT_FACTOR);

    return (
        <div className="payment-page">
            <div className="payment-container">
                <div className="payment-form-section">
                    <button type="button" className="back-button" onClick={() => navigate('/plan')}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to plans
                    </button>

                    <h1>Complete your purchase</h1>
                    <p className="form-subtitle">
                        Pay securely with Razorpay using a credit or debit card.
                    </p>

                    <div className="payment-form">
                        <div className="form-group">
                            <label>Account email</label>
                            <input
                                type="email"
                                value={userData?.email || ''}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                            />
                        </div>

                        {userData?.mobile ? (
                            <div className="form-group">
                                <label>Mobile number</label>
                                <input
                                    type="tel"
                                    value={userData?.mobile}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                />
                            </div>
                        ) : null}

                        <div className="recurring-notice" role="note">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M23 4v6h-6M1 20v-6h6" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
                            </svg>
                            <div>
                                <strong>This is a recurring subscription.</strong> Your card will be securely
                                saved and automatically charged each {planDetails?.billingCycle === 'annual' ? 'year' : 'month'} based
                                on your usage, until you cancel. You can cancel anytime from the Billing page.
                            </div>
                        </div>

                        <button
                            type="button"
                            className={`pay-button ${processing ? 'processing' : ''}`}
                            disabled={processing}
                            onClick={handlePay}
                        >
                            {processing ? (
                                <>
                                    <span className="spinner"></span>
                                    Opening payment...
                                </>
                            ) : (
                                `Pay ${formattedTotal}`
                            )}
                        </button>

                        <p className="secure-text">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                            Payments processed securely by Razorpay
                        </p>
                    </div>
                </div>

                <div className="order-summary-section">
                    <div className="order-summary-card">
                        <h3>Order Summary</h3>

                        <div className="plan-info">
                            <div className="plan-badge">{planDetails.name} Plan</div>
                            <p className="plan-billing">
                                {planDetails.billingCycle === 'annual' ? 'Annual billing' : 'Monthly billing'}
                            </p>
                        </div>

                        <div className="summary-divider"></div>

                        <div className="summary-row">
                            <span>{planDetails.name} plan</span>
                            <span>{planDetails.price}{planDetails.period || '/mo'}</span>
                        </div>

                        {planDetails.billingCycle === 'annual' && annualDiscount > 0 && (
                            <div className="summary-row discount">
                                <span>Annual discount (20%)</span>
                                <span className="discount-amount">-{formatMetricPrice(currencySymbol, annualDiscount)}</span>
                            </div>
                        )}

                        <div className="summary-divider"></div>

                        <div className="summary-row total">
                            <span>Total due today</span>
                            <span>{formattedTotal}</span>
                        </div>

                        {planDetails.pricing?.length > 0 && (
                            <div className="summary-features">
                                <h4>Usage rates:</h4>
                                <PlanMetricsList
                                    pricing={planDetails.pricing}
                                    currencySymbol={currencySymbol}
                                    showFreeLimits={false}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Payment;
